import { useEffect } from 'react'
import { useHealthProbes } from '@hooks/useHealthProbes'
import { useOfferStore } from '@store/offerStore'
import styles from './HealthDashboard.module.css'

const STATUS_ICONS = { ok: '✅', degraded: '⚠️', down: '❌' }
const STRATEGY_LABELS = {
  'api-json': 'API → JSON',
  'intershop-api': 'INTERSHOP REST',
  'nuxt-data': 'Nuxt __NUXT_DATA__',
  'ssr-html': 'SSR HTML',
  'nuxt-ssr-articles': 'Nuxt SSR + <article>',
  'schwarz-flyer-api': 'Schwarz Flyer API',
  'next-html': 'Next.js HTML',
  'api-authenticated': 'API (auth-gated)',
  stub: 'Stub',
}
const STEP_LABELS = {
  auth: 'Authentication',
  search: 'Product Search',
  fetch: 'HTTP Fetch',
  parse: 'HTML Parsing',
  extraction: 'Data Extraction',
  connect: 'Network Connection',
}

const BADGE_STYLES = {
  ok: styles.badgeOk,
  degraded: styles.badgeDegraded,
  down: styles.badgeDown,
}

const SUMMARY_STYLES = {
  healthy: styles.summaryHealthy,
  degraded: styles.summaryDegraded,
  unhealthy: styles.summaryUnhealthy,
}

function StatusBadge({ status }) {
  return (
    <span className={`${styles.componentStatusBadge} ${BADGE_STYLES[status] || ''}`}>
      {STATUS_ICONS[status] || '❓'} {status}
    </span>
  )
}

function TimingBar({ label, ms }) {
  if (ms === null || ms === undefined) return null
  const width = Math.min((ms / 5000) * 100, 100)

  return (
    <div className={styles.componentTimingBar}>
      <span className={styles.timingLabel}>{label}</span>
      <div className={styles.timingTrack}>
        <div
          className={styles.timingFill}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className={styles.timingValue}>{ms}ms</span>
    </div>
  )
}

function DiagnosticsBlock({ diagnostics, layoutClassName = undefined }) {
  if (!diagnostics) return null

  const { failedStep, responseSnippet, responseHeaders, config, suggestions } = diagnostics

  const failedStepBlock = failedStep && (
    <FailedStepInfo step={failedStep} />
  )
  const suggestionsBlock = suggestions?.length > 0 && (
    <SuggestionsList {...{ suggestions }} />
  )
  const configBlock = config && (
    <ConfigInfo {...{ config }} />
  )
  const headersBlock = responseHeaders && (
    <ResponseHeadersInfo headers={responseHeaders} />
  )
  const snippetBlock = responseSnippet && (
    <ResponseSnippet snippet={responseSnippet} />
  )

  return (
    <div className={`${styles.componentDiagnosticsBlock} ${layoutClassName || ''}`}>
      <span className={styles.sectionLabel}>Diagnostics</span>
      {failedStepBlock}
      {suggestionsBlock}
      {configBlock}
      {headersBlock}
      {snippetBlock}
    </div>
  )
}

function FailedStepInfo({ step }) {
  return (
    <div className={styles.componentFailedStepInfo}>
      ⛔ Failed at: <strong className={styles.boldText}>{STEP_LABELS[step] || step}</strong>
    </div>
  )
}

function SuggestionsList({ suggestions }) {
  return (
    <ul className={styles.componentSuggestionsList}>
      {suggestions.map((s) => (
        <li key={s} className={styles.suggestionItem}>💡 {s}</li>
      ))}
    </ul>
  )
}

function ConfigInfo({ config }) {
  return (
    <div className={styles.componentConfigInfo}>
      {Object.entries(config).map(([key, val]) => (
        <div key={key} className={styles.configItem}>
          <strong className={styles.boldText}>{key}:</strong> {JSON.stringify(val)}
        </div>
      ))}
    </div>
  )
}

function ResponseHeadersInfo({ headers }) {
  return (
    <div className={styles.componentResponseHeadersInfo}>
      {Object.entries(headers).map(([key, val]) => (
        <div key={key} className={styles.headerRow}>
          <span className={styles.headerKey}>{key}:</span>
          <span className={styles.headerValue}>{val}</span>
        </div>
      ))}
    </div>
  )
}

function ResponseSnippet({ snippet }) {
  return (
    <div className={styles.componentResponseSnippet}>
      <span className={styles.snippetLabel}>Response body</span>
      <pre className={styles.snippetBody}>{snippet}</pre>
    </div>
  )
}

function HttpSection({ http }) {
  if (!http) return null

  const statusClass = http.status >= 200 && http.status < 400
    ? styles.okText
    : styles.errText

  const endpointSlot = http.endpoint && (
    <span className={styles.muted}> · {http.endpoint}</span>
  )
  const bytesSlot = http.bytes > 0 && (
    <span className={styles.muted}> · {formatBytes(http.bytes)}</span>
  )

  return (
    <div className={styles.componentHttpSection}>
      <span className={styles.sectionLabel}>HTTP</span>
      <div className={styles.mono}>
        <span className={statusClass}>{http.status}</span>
        {endpointSlot}
        {bytesSlot}
      </div>
    </div>
  )
}

function ExtractionSection({ extraction }) {
  if (!extraction) return null

  const strategyLabel = STRATEGY_LABELS[extraction.strategy] || extraction.strategy
  const hasRawCount = extraction.rawCount !== null
  const hasBioCount = extraction.bioCount !== undefined && extraction.bioCount !== null
  const rawCountClass = extraction.rawCount > 0 ? styles.okText : styles.errText

  const rawCountSlot = hasRawCount && (
    <span>
      {' → '}
      <strong className={rawCountClass}>
        {extraction.rawCount}
      </strong>
      {' products'}
    </span>
  )
  const bioCountSlot = hasBioCount && (
    <span className={styles.muted}> ({extraction.bioCount} bio)</span>
  )
  const noteSlot = extraction.note && (
    <div className={styles.note}>{extraction.note}</div>
  )

  return (
    <div className={styles.componentExtractionSection}>
      <span className={styles.sectionLabel}>Extraction</span>
      <div className={styles.mono}>
        <span className={styles.strategy}>{strategyLabel}</span>
        {rawCountSlot}
        {bioCountSlot}
      </div>
      {noteSlot}
    </div>
  )
}

function SamplesSection({ sampleNames }) {
  if (!sampleNames?.length) return null

  return (
    <div className={styles.componentSamplesSection}>
      <span className={styles.sectionLabel}>Samples</span>
      <ul className={styles.samples}>
        {sampleNames.map((name) => (
          <li key={name} className={styles.sampleItem}>{name}</li>
        ))}
      </ul>
    </div>
  )
}

function StructureSection({ structure }) {
  if (!structure) return null

  const fingerprintSlot = structure.fingerprint && (
    <div className={`${styles.mono} ${styles.fingerprint}`}>
      {structure.fingerprint}
    </div>
  )
  const warningsSlot = structure.warnings?.length > 0 && (
    <ul className={styles.warnings}>
      {structure.warnings.map((w) => (
        <li key={w} className={styles.warningItem}>⚠ {w}</li>
      ))}
    </ul>
  )

  return (
    <div className={styles.componentStructureSection}>
      <span className={styles.sectionLabel}>Structure</span>
      {fingerprintSlot}
      {warningsSlot}
    </div>
  )
}

function TimingSection({ timing }) {
  if (!timing) return null

  return (
    <div className={styles.componentTimingSection}>
      <span className={styles.sectionLabel}>Timing</span>
      <TimingBar label="fetch" ms={timing.fetchMs} />
      <TimingBar label="parse" ms={timing.extractMs} />
      <TimingBar label="total" ms={timing.totalMs} />
    </div>
  )
}

function ProbeCard({ scraper }) {
  const { extraction, structure, timing, error, diagnostics } = scraper
  const showDiagnostics = scraper.status === 'down' || scraper.status === 'degraded'
  const cardClass = scraper.status === 'down'
    ? `${styles.componentProbeCard} ${styles.cardDown}`
    : styles.componentProbeCard

  const errorSlot = error && (
    <div className={styles.cardError}>{error}</div>
  )
  const diagnosticsSlot = showDiagnostics && (
    <DiagnosticsBlock {...{ diagnostics }} layoutClassName={styles.diagnosticsBlockLayout} />
  )

  return (
    <div className={cardClass}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardName}>{scraper.name}</h3>
        <StatusBadge status={scraper.status} />
      </div>

      {errorSlot}

      <HttpSection http={scraper.http} />
      <ExtractionSection {...{ extraction }} />
      <SamplesSection sampleNames={extraction?.sampleNames} />
      {diagnosticsSlot}
      <StructureSection {...{ structure }} />
      <TimingSection {...{ timing }} />
    </div>
  )
}

function SummaryBar({ summary, layoutClassName = undefined }) {
  if (!summary) return null

  const overallStatus =
    summary.overall === 'healthy' ? 'ok' :
    summary.overall === 'degraded' ? 'degraded' :
    'down'

  const summaryStyle = SUMMARY_STYLES[summary.overall] || ''

  const degradedText = summary.degraded > 0 && (
    <> · <span className={styles.warnText}>{summary.degraded} degraded</span></>
  )
  const downText = summary.down > 0 && (
    <> · <span className={styles.errText}>{summary.down} down</span></>
  )

  return (
    <div className={`${styles.componentSummaryBar} ${summaryStyle} ${layoutClassName || ''}`}>
      <StatusBadge status={overallStatus} />
      <span className={styles.summaryText}>
        <strong className={styles.boldText}>{summary.healthy}</strong>/{summary.total} healthy
        {degradedText}
        {downText}
      </span>
    </div>
  )
}

function ErrorBlock({ error, onRefresh }) {
  return (
    <div className={styles.componentErrorBlock}>
      <p>❌ {error}</p>
      <button className={styles.refresh} onClick={onRefresh} type="button">
        Probeer opnieuw
      </button>
    </div>
  )
}

function LoadingBlock() {
  return (
    <div className={styles.componentLoadingBlock}>
      <div className={styles.spinner} />
      <p>Running probes against all supermarket sources...</p>
    </div>
  )
}

function ScrapersGrid({ scrapers }) {
  return (
    <div className={styles.componentScrapersGrid}>
      {scrapers.map(scraper => (
        <ProbeCard key={scraper.id} {...{ scraper }} />
      ))}
    </div>
  )
}

function DashboardHeader({ data, loading, refresh, layoutClassName = undefined }) {
  const timestamp = data?.checkedAt
    ? <span className={styles.timestamp}>{formatTimestamp(data.checkedAt)}</span>
    : null

  const buttonLabel = loading ? '⟳ Probing...' : '⟳ Run probes'

  return (
    <div className={`${styles.componentDashboardHeader} ${layoutClassName || ''}`}>
      <div className={styles.titleGroup}>
        <h2 className={styles.title}>🩺 Parser Health</h2>
        <p className={styles.subtitle}>
          Live diagnostics for all supermarket scrapers
        </p>
      </div>

      <div className={`${styles.actions} ${styles.actionsLayout}`}>
        {timestamp}
        <button
          className={styles.refresh}
          onClick={refresh}
          disabled={loading}
          type="button"
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  )
}

function CatalogSubscriptionInfo({ hasAHPremium }) {
  const premiumLabel = hasAHPremium
    ? <><span className={styles.catalogAccent}>💎 AH Premium</span> — 10% korting op bio</>
    : 'Niet ingeschakeld'

  return (
    <div className={styles.componentCatalogSubscriptionInfo}>
      <span className={styles.sectionLabel}>Abonnement</span>
      <div className={styles.mono}>{premiumLabel}</div>
    </div>
  )
}

function CatalogProductsInfo({ catalog, totalProducts }) {
  return (
    <div className={styles.componentCatalogProductsInfo}>
      <span className={styles.sectionLabel}>Producten</span>
      <div className={styles.mono}>
        <strong className={styles.catalogAccent}>{totalProducts}</strong> bio producten
        {Object.entries(catalog.supermarkets).map(([key, val]) => (
          <span key={key} className={styles.muted}> · {key}: {val.count}</span>
        ))}
      </div>
    </div>
  )
}

function CatalogFetchInfo({ catalog, isStale }) {
  const staleWarning = isStale && (
    <span className={styles.warnText}> · verouderd (&gt;7 dagen)</span>
  )

  return (
    <div className={styles.componentCatalogFetchInfo}>
      <span className={styles.sectionLabel}>Laatst opgehaald</span>
      <div className={styles.mono}>
        {catalog.fetchedAt ? formatTimestamp(catalog.fetchedAt) : '—'}
        {staleWarning}
      </div>
    </div>
  )
}

function CatalogErrors({ errors }) {
  if (!errors?.length) return null

  return (
    <div className={styles.componentCatalogErrors}>
      {errors.map((e, i) => (
        <div key={i}>{e.supermarket}: {e.error}</div>
      ))}
    </div>
  )
}

function deriveCatalogStatus(catalog, catalogLoading) {
  const hasCatalog = catalog?.supermarkets !== null && catalog?.supermarkets !== undefined
  const totalProducts = hasCatalog
    ? Object.values(catalog.supermarkets).reduce((sum, s) => sum + s.count, 0)
    : 0
  const statusBadge = catalogLoading ? 'loading' : hasCatalog ? 'ok' : 'missing'
  const isStale = hasCatalog && catalog.fetchedAt
    ? (Date.now() - new Date(catalog.fetchedAt).getTime()) > 7 * 24 * 60 * 60 * 1000
    : false

  return { hasCatalog, totalProducts, statusBadge, isStale }
}

function CatalogStatusCard() {
  const catalog = useOfferStore(s => s.catalog)
  const hasAHPremium = useOfferStore(s => s.hasAHPremium)
  const catalogLoading = useOfferStore(s => s.catalogLoading)

  const { hasCatalog, totalProducts, statusBadge, isStale } = deriveCatalogStatus(catalog, catalogLoading)

  const catalogDetails = hasCatalog && (
    <>
      <CatalogProductsInfo {...{ catalog, totalProducts }} />
      <CatalogFetchInfo {...{ catalog, isStale }} />
      <CatalogErrors errors={catalog.errors} />
    </>
  )

  const emptyNote = !hasCatalog && !catalogLoading && (
    <div className={styles.note}>
      Wordt automatisch opgehaald wanneer Premium wordt ingeschakeld via de 💎 knop in de header.
    </div>
  )

  return (
    <div className={styles.componentCatalogStatusCard}>
      <CatalogCardHeader {...{ statusBadge }} />
      <CatalogSubscriptionInfo {...{ hasAHPremium }} />
      {catalogDetails}
      {emptyNote}
    </div>
  )
}

function CatalogCardHeader({ statusBadge }) {
  const CATALOG_BADGE = {
    ok: { icon: '✅', label: 'ok', className: styles.badgeOk },
    loading: { icon: '⏳', label: 'laden...', className: '' },
    missing: { icon: '💤', label: 'niet geladen', className: '' },
  }
  const badge = CATALOG_BADGE[statusBadge]

  return (
    <div className={styles.componentCatalogCardHeader}>
      <h3 className={styles.cardName}>Albert Heijn</h3>
      <StatusBadge status={badge.icon === '✅' ? 'ok' : 'loading'} />
    </div>
  )
}

function SectionHeader({ icon, title, subtitle, accentClass = undefined }) {
  return (
    <div className={`${styles.componentSectionHeader} ${accentClass || ''}`}>
      <div className={styles.sectionHeaderLeft}>
        <span className={styles.sectionHeaderIcon}>{icon}</span>
        <h3 className={styles.sectionHeaderTitle}>{title}</h3>
      </div>
      {subtitle && <p className={styles.sectionHeaderSubtitle}>{subtitle}</p>}
    </div>
  )
}

export function HealthDashboard({ layoutClassName = undefined }) {
  const { data, loading, error, refresh } = useHealthProbes()

  // Fetch on mount
  useEffect(() => { refresh() }, [refresh])

  const errorBlock = error && !data && (
    <ErrorBlock {...{ error }} onRefresh={refresh} />
  )
  const loadingBlock = loading && !data && (
    <LoadingBlock />
  )

  return (
    <div className={`${styles.component} ${layoutClassName || ''}`}>
      <DashboardHeader {...{ data, loading, refresh }} layoutClassName={styles.dashboardHeaderLayout} />

      <SummaryBar summary={data?.summary} layoutClassName={styles.summaryBarLayout} />

      {errorBlock}
      {loadingBlock}

      {/* Scrapers section */}
      <div className={styles.section}>
        <SectionHeader
          icon="🔍"
          title="Scrapers"
          subtitle="Wekelijkse bonus-aanbiedingen per supermarkt"
        />
        {data?.scrapers && <ScrapersGrid scrapers={data.scrapers} />}
      </div>

      {/* Catalog section */}
      <div className={`${styles.section} ${styles.sectionCatalog}`}>
        <SectionHeader
          icon="📦"
          title="Catalog"
          subtitle="Reguliere schapprijzen voor abonnementsvergelijking"
          accentClass={styles.sectionHeaderCatalog}
        />
        <CatalogStatusCard />
      </div>
    </div>
  )
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

function formatTimestamp(iso) {
  try {
    const date = new Date(iso)
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return iso
  }
}
